import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ResizablePanelGroup, 
  ResizablePanel, 
  ResizableHandle 
} from "@/components/ui/resizable";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, 
  ArrowRight, 
  FolderOpen, 
  Calculator, 
  ExternalLink, 
  Users,
  Briefcase,
  Shield,
  Zap,
  TrendingUp,
  Home,
  Wallet,
  Handshake,
  ClipboardList,
  Star,
  FileSearch,
  Target,
  Check,
  StickyNote,
  Pencil,
  Settings,
  Edit3,
  Link2,
  Copy,
  CheckCircle2,
  History,
  Calendar
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RichTextEditorChecklist } from "@/components/ui/rich-text-editor-checklist";
import { DrawingCanvas, DrawingContent } from "./DrawingCanvas";
import { useBeratungModule, BeratungModul } from "@/hooks/useBeratungModule";
import { useBeratungSeiten, BeratungSeite } from "@/hooks/useBeratungSeiten";
import { useFilteredSeiten } from "@/hooks/useFilteredSeiten";
import { useBeratungSession, useUpdateBeratungSession } from "@/hooks/useBeratungSessions";
import { BeraterPanel } from "./BeraterPanel";
import { KundenPanel } from "./KundenPanel";
import { BeratungMobileLayout } from "./BeratungMobileLayout";
import { AutoSaveIndicator } from "./AutoSaveIndicator";
import { BeratungSidebar } from "./BeratungSidebar";
import { BeratungHistoryDialog } from "./BeratungHistoryDialog";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUserRole } from "@/hooks/useUserRole";
import { cn } from "@/lib/utils";
import { PanelLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { fromTable } from "@/integrations/supabase/db";

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// Helper to safely parse drawing content from DB
function parseDrawingContent(data: unknown): DrawingContent {
  if (data && typeof data === 'object' && 'drawing' in data && 'text' in data) {
    return { drawing: String((data as any).drawing || ''), text: String((data as any).text || '') };
  }
  return { drawing: '', text: '' };
}

// Icon mapping for dynamic icons
const ICON_MAP: Record<string, React.ReactNode> = {
  Handshake: <Handshake className="h-6 w-6" />,
  ClipboardList: <ClipboardList className="h-6 w-6" />,
  Star: <Star className="h-6 w-6" />,
  FileSearch: <FileSearch className="h-6 w-6" />,
  Target: <Target className="h-6 w-6" />,
  Briefcase: <Briefcase className="h-6 w-6" />,
  Shield: <Shield className="h-6 w-6" />,
  Wallet: <Wallet className="h-6 w-6" />,
  Zap: <Zap className="h-6 w-6" />,
  TrendingUp: <TrendingUp className="h-6 w-6" />,
  Home: <Home className="h-6 w-6" />,
};

interface BeratungLayoutProps {
  sessionId?: string;
}

export function BeratungLayout({ sessionId }: BeratungLayoutProps) {
  const navigate = useNavigate();
  const updateSession = useUpdateBeratungSession();
  const isMobile = useIsMobile();
  const { isAdmin } = useUserRole();

  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isStarted, setIsStarted] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isNotizOpen, setIsNotizOpen] = useState(false);
  const [interneNotiz, setInterneNotiz] = useState<string>('');
  const [isDrawingOpen, setIsDrawingOpen] = useState(false);
  const [drawingContent, setDrawingContent] = useState<DrawingContent>({ drawing: '', text: '' });
  const [isDrawingShared, setIsDrawingShared] = useState(false);
  const [isDrawingCollaborative, setIsDrawingCollaborative] = useState(false);
  const [isEditModeActive, setIsEditModeActive] = useState(false);
  const [tileOverrides, setTileOverrides] = useState<Record<string, any[]>>({});
  const [hoveredTileId, setHoveredTileId] = useState<string | null>(null);
  const [isRenamingSession, setIsRenamingSession] = useState(false);
  const [sessionName, setSessionName] = useState<string>('');
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState<string>('');
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [sessionDate, setSessionDate] = useState<Date | undefined>(undefined);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedValuesRef = useRef<string>('');
  const hydratedSessionIdRef = useRef<string | null>(null);
  const drawingHydratedRef = useRef<string | null>(null);
  const drawingDirtyRef = useRef(false);
  const latestDrawingRef = useRef<DrawingContent>({ drawing: '', text: '' });
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const latestFormValuesRef = useRef<Record<string, any>>({});
  const latestIsDrawingSharedRef = useRef(false);
  const latestIsDrawingCollaborativeRef = useRef(false);
  const latestCurrentSeiteIdRef = useRef<string | undefined>();
  
  // Callback ref that always returns current state values - solves race condition on initial sync
  const getBroadcastStateRef = useRef<() => {
    drawingContent: DrawingContent;
    isDrawingShared: boolean;
    isDrawingCollaborative: boolean;
    formValues: Record<string, any>;
    currentSeiteId: string | undefined;
    currentSeiteData: BeratungSeite | null;
  }>(() => ({
    drawingContent: { drawing: '', text: '' },
    isDrawingShared: false,
    isDrawingCollaborative: false,
    formValues: {},
    currentSeiteId: undefined,
    currentSeiteData: null,
  }));

  // Load existing session (when opened from "Bisherige Beratungen")
  const { data: session } = useBeratungSession({
    sessionId,
    enabled: !!sessionId,
  });

  // Load modules
  const { data: alleModule = [], isLoading: modulesLoading } = useBeratungModule({});
  
  // Load pages for selected modules
  const { data: allSeitenUnfiltered = [], isLoading: seitenLoading } = useBeratungSeiten({
    modulIds: selectedModules,
    enabled: isStarted && selectedModules.length > 0,
  });

  // Filter pages based on selected priorities (dynamic step filtering)
  const allSeiten = useFilteredSeiten(allSeitenUnfiltered, formValues);

  // Current page with tile overrides applied
  const currentSeiteRaw = allSeiten[currentPageIndex] || null;
  const currentSeite = useMemo(() => {
    if (!currentSeiteRaw) return null;
    const tilesOverride = tileOverrides[currentSeiteRaw.id];
    if (!tilesOverride) return currentSeiteRaw;
    // Apply tile overrides to the kunden_layout
    return {
      ...currentSeiteRaw,
      kunden_layout: {
        ...currentSeiteRaw.kunden_layout,
        tiles: tilesOverride,
      },
    };
  }, [currentSeiteRaw, tileOverrides]);
  
  const totalPages = allSeiten.length;
  const progress = totalPages > 0 ? ((currentPageIndex + 1) / totalPages) * 100 : 0;
  
  // Handler for tile updates in edit mode - persist to beratung_seiten (global, not session-specific)
  const handleTilesUpdate = useCallback(async (tiles: any[]) => {
    if (!currentSeiteRaw) return;
    
    // Update the kunden_layout.tiles in the beratung_seiten table (global for all sessions)
    const updatedKundenLayout = {
      ...currentSeiteRaw.kunden_layout,
      tiles,
    };
    
    try {
      const { error } = await fromTable('beratung_seiten')
        .update({ kunden_layout: updatedKundenLayout })
        .eq('id', currentSeiteRaw.id);
      
      if (error) {
        console.error('Error saving tile layout:', error);
        return;
      }
      
      // Also broadcast for same-browser sync
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'beratung-sync',
          currentSeiteData: {
            ...currentSeiteRaw,
            kunden_layout: updatedKundenLayout,
          },
        });
      }
    } catch (err) {
      console.error('Error saving tile layout:', err);
    }
  }, [currentSeiteRaw]);

  // Handler for tile selection in customer view - persist to DB for cross-device sync
  const handleTileSelect = useCallback((tileId: string) => {
    setFormValues(prev => {
      const currentPriorities: string[] = prev.selected_priorities || [];
      const isSelected = currentPriorities.includes(tileId);
      const newPriorities = isSelected
        ? currentPriorities.filter(id => id !== tileId)
        : [...currentPriorities, tileId];
      
      const newFormValues = { ...prev, selected_priorities: newPriorities };
      
      // Persist to DB for cross-device sync
      if (sessionId) {
        updateSession.mutate({
          id: sessionId,
          form_values: newFormValues,
        });
      }
      
      // Also send via BroadcastChannel for same-browser performance
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'beratung-sync',
          formValues: newFormValues,
        });
      }
      
      return newFormValues;
    });
  }, [sessionId, updateSession]);

  // Hydrate session state (modules, isStarted, tileOverrides, formValues, name) - only once per sessionId
  useEffect(() => {
    if (!sessionId || !session) return;
    if (hydratedSessionIdRef.current === sessionId) return;

    hydratedSessionIdRef.current = sessionId;
    const modules = session.ausgewaehlte_module || [];
    setSelectedModules(modules);
    setIsStarted(modules.length > 0);
    setCurrentPageIndex(0);
    
    // Hydrate session name and date
    setSessionName(session.name || '');
    setSessionDate(session.gestartet_am ? new Date(session.gestartet_am) : new Date());
    
    // Hydrate tile_overrides and form_values from DB
    if (session.tile_overrides && Object.keys(session.tile_overrides).length > 0) {
      setTileOverrides(session.tile_overrides);
    }
    if (session.form_values && Object.keys(session.form_values).length > 0) {
      setFormValues(session.form_values);
    }
  }, [sessionId, session?.id]);

  // Hydrate drawing content - runs when session data changes (including refetch)
  // Only hydrate if local changes haven't been made (dirty flag)
  useEffect(() => {
    if (!sessionId || !session) return;
    
    // Skip hydration if local changes are pending
    if (drawingDirtyRef.current) return;
    
    const serverDrawing = parseDrawingContent(session.drawing_content);
    const serverKey = `${sessionId}:${session.updated_at ?? ''}`;
    
    // Skip if we've already hydrated this exact server state
    if (drawingHydratedRef.current === serverKey) return;
    
    // Skip if server has no content
    if (!serverDrawing.drawing && !serverDrawing.text) return;
    
    // Hydrate from server
    drawingHydratedRef.current = serverKey;
    setDrawingContent(serverDrawing);
    latestDrawingRef.current = serverDrawing;
  }, [sessionId, session?.drawing_content, session?.updated_at]);

  // Reset page index if current page is filtered out
  useEffect(() => {
    if (currentPageIndex >= allSeiten.length && allSeiten.length > 0) {
      setCurrentPageIndex(allSeiten.length - 1);
    }
  }, [allSeiten.length, currentPageIndex]);

  // Restore last page when we know the pages list (only on initial load)
  const initialPageRestoredRef = useRef(false);
  useEffect(() => {
    if (initialPageRestoredRef.current) return;
    if (!session?.aktuelle_seite_id) return;
    if (!allSeiten.length) return;

    const idx = allSeiten.findIndex((s) => s.id === session.aktuelle_seite_id);
    if (idx >= 0) {
      setCurrentPageIndex(idx);
      initialPageRestoredRef.current = true;
    }
  }, [session?.aktuelle_seite_id, allSeiten]);

  // Persist current page (so a session can be continued later)
  useEffect(() => {
    if (!sessionId) return;
    if (!isStarted) return;
    if (!currentSeite?.id) return;

    updateSession.mutate({
      id: sessionId,
      aktuelle_seite_id: currentSeite.id,
      status: 'aktiv',
    });
  }, [sessionId, isStarted, currentSeite?.id]);

  // Group modules
  const gruppe1Module = useMemo(() => 
    alleModule.filter(m => m.gruppe === 'gruppe1_allgemein'),
    [alleModule]
  );
  
  const gruppe2Module = useMemo(() => 
    alleModule.filter(m => m.gruppe === 'gruppe2_fachbereiche'),
    [alleModule]
  );

  const toggleModule = (modulId: string) => {
    setSelectedModules(prev => 
      prev.includes(modulId) 
        ? prev.filter(id => id !== modulId)
        : [...prev, modulId]
    );
  };

  const handleStartBeratung = () => {
    if (selectedModules.length === 0) return;
    setIsStarted(true);
    setCurrentPageIndex(0);

    // Persist chosen modules for this session (enables "Weiterführen")
    if (sessionId) {
      updateSession.mutate({
        id: sessionId,
        ausgewaehlte_module: selectedModules,
        status: 'aktiv',
      });
    }
  };

  const handleEndBeratung = () => {
    // Return to module selection ("Beratungsauswahl") instead of leaving the Beratung route.
    if (sessionId) {
      updateSession.mutate({
        id: sessionId,
        status: 'pausiert',
        aktuelle_seite_id: currentSeite?.id ?? null,
        ausgewaehlte_module: selectedModules,
      });
    }

    setIsStarted(false);
    setCurrentPageIndex(0);
  };

  // Handle session rename
  const handleSaveSessionName = useCallback(() => {
    if (!sessionId) return;
    
    updateSession.mutate({
      id: sessionId,
      name: sessionName.trim() || null,
    });
    setIsRenamingSession(false);
  }, [sessionId, sessionName, updateSession]);

  // Handle session date update
  const handleSaveSessionDate = useCallback((date: Date | undefined) => {
    if (!sessionId || !date) return;
    
    updateSession.mutate({
      id: sessionId,
      gestartet_am: date.toISOString(),
    });
    setSessionDate(date);
    setIsDatePickerOpen(false);
  }, [sessionId, updateSession]);

  // Flush pending save immediately (used when closing dialog or unmounting)
  const flushSave = useCallback(async () => {
    if (!sessionId || !isStarted) return;
    
    // Clear pending debounce
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    // Use latest ref values to avoid stale closures
    const currentDrawing = latestDrawingRef.current;
    const currentValuesStr = JSON.stringify({ formValues, drawingContent: currentDrawing });
    if (currentValuesStr === lastSavedValuesRef.current) return;
    
    setSaveStatus('saving');
    try {
      await updateSession.mutateAsync({
        id: sessionId,
        drawing_content: currentDrawing,
      });
      lastSavedValuesRef.current = currentValuesStr;
      drawingDirtyRef.current = false;
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus('error');
    }
  }, [sessionId, isStarted, formValues, updateSession]);

  // Direct save for drawing content (shorter debounce for drawings)
  // Includes shared/collaborative flags in the JSONB for customer tab polling
  const saveDrawingContent = useCallback((content: DrawingContent) => {
    if (!sessionId || !isStarted) return;
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    setSaveStatus('saving');
    
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        // Include sharing flags in drawing_content for DB polling fallback
        const contentWithFlags = {
          ...content,
          shared: latestIsDrawingSharedRef.current,
          collaborative: latestIsDrawingCollaborativeRef.current,
        };
        await updateSession.mutateAsync({
          id: sessionId,
          drawing_content: contentWithFlags as any,
        });
        drawingDirtyRef.current = false;
        lastSavedValuesRef.current = JSON.stringify({ formValues, drawingContent: content });
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
      } catch (err) {
        console.error('Save error:', err);
        setSaveStatus('error');
      }
    }, 500); // Shorter debounce for drawings
  }, [sessionId, isStarted, formValues, updateSession]);

  // Handler for drawing content changes - marks dirty and triggers save
  const handleDrawingContentChange = useCallback((content: DrawingContent) => {
    setDrawingContent(content);
    latestDrawingRef.current = content;
    drawingDirtyRef.current = true;
    saveDrawingContent(content);
  }, [saveDrawingContent]);

  // Handler for sharing toggle - updates state and triggers save with new flag
  const handleSharingChange = useCallback((sharing: boolean) => {
    setIsDrawingShared(sharing);
    latestIsDrawingSharedRef.current = sharing;
    // If sharing is turned off, also disable collaboration
    if (!sharing) {
      setIsDrawingCollaborative(false);
      latestIsDrawingCollaborativeRef.current = false;
    }
    // Trigger save to persist the flags
    saveDrawingContent(latestDrawingRef.current);
  }, [saveDrawingContent]);

  // Handler for collaboration toggle - updates state and triggers save
  const handleCollaborativeChange = useCallback((collaborative: boolean) => {
    setIsDrawingCollaborative(collaborative);
    latestIsDrawingCollaborativeRef.current = collaborative;
    // Trigger save to persist the flags
    saveDrawingContent(latestDrawingRef.current);
  }, [saveDrawingContent]);

  // Ref for saveDrawingContent to avoid recreating BroadcastChannel
  const saveDrawingContentRef = useRef(saveDrawingContent);
  useEffect(() => { saveDrawingContentRef.current = saveDrawingContent; }, [saveDrawingContent]);

  // BroadcastChannel for syncing with pop-out customer tab
  // CRITICAL: Only depend on sessionId to prevent channel recreation
  useEffect(() => {
    if (!sessionId) return;
    
    broadcastChannelRef.current = new BroadcastChannel(`beratung-sync-${sessionId}`);
    
    // Listen for messages from customer tab
    broadcastChannelRef.current.onmessage = (event) => {
      // Customer tab ready - send current state
      if (event.data.type === 'beratung-sync-ready') {
        // Use callback ref to get CURRENT state values (solves race condition)
        const state = getBroadcastStateRef.current();
        broadcastChannelRef.current?.postMessage({
          type: 'beratung-sync',
          drawingContent: state.drawingContent,
          isDrawingShared: state.isDrawingShared,
          isDrawingCollaborative: state.isDrawingCollaborative,
          formValues: state.formValues,
          currentSeiteId: state.currentSeiteId,
          currentSeiteData: state.currentSeiteData,
        });
      }
      // Collaborative drawing updates from customer
      if (event.data.type === 'beratung-sync-reply') {
        if (event.data.drawingContent) {
          setDrawingContent(event.data.drawingContent);
          latestDrawingRef.current = event.data.drawingContent;
          drawingDirtyRef.current = true;
          saveDrawingContentRef.current(event.data.drawingContent);
        }
        // Sync form values (including priorities) from customer tab
        if (event.data.formValues) {
          setFormValues(event.data.formValues);
        }
      }
      // Hover sync from customer tab
      if (event.data.type === 'beratung-hover-sync') {
        setHoveredTileId(event.data.hoveredTileId ?? null);
      }
    };
    
    return () => {
      broadcastChannelRef.current?.close();
      broadcastChannelRef.current = null;
    };
  }, [sessionId]);

  // Supabase Realtime for cross-device synchronization (bidirectional with customer views)
  useEffect(() => {
    if (!sessionId) return;
    
    const channel = supabase
      .channel(`session-realtime-${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'crm',
          table: 'beratung_sessions',
          filter: `id=eq.${sessionId}`,
        },
        (payload) => {
          const newData = payload.new as any;
          
          // Sync tile_overrides from other devices
          if (newData.tile_overrides && Object.keys(newData.tile_overrides).length > 0) {
            setTileOverrides(newData.tile_overrides);
          }
          
          // Sync form_values from other devices
          if (newData.form_values && Object.keys(newData.form_values).length > 0) {
            setFormValues(newData.form_values);
          }
          
          // Sync current page from other devices
          if (newData.aktuelle_seite_id) {
            const idx = allSeiten.findIndex(s => s.id === newData.aktuelle_seite_id);
            if (idx >= 0 && idx !== currentPageIndex) {
              setCurrentPageIndex(idx);
            }
          }
          
          // Sync drawing_content from customer devices (bidirectional collaboration)
          // Only apply if local changes aren't pending and collaborative mode is active
          if (newData.drawing_content && latestIsDrawingCollaborativeRef.current) {
            // Skip if we are currently saving (to avoid loops)
            if (!drawingDirtyRef.current) {
              const parsed = parseDrawingContent(newData.drawing_content);
              // Only update if different from local state
              const currentKey = JSON.stringify(latestDrawingRef.current);
              const newKey = JSON.stringify(parsed);
              if (currentKey !== newKey) {
                setDrawingContent(parsed);
                latestDrawingRef.current = parsed;
                // Update sharing flags from the payload
                if (typeof newData.drawing_content.shared === 'boolean') {
                  setIsDrawingShared(newData.drawing_content.shared);
                  latestIsDrawingSharedRef.current = newData.drawing_content.shared;
                }
                if (typeof newData.drawing_content.collaborative === 'boolean') {
                  setIsDrawingCollaborative(newData.drawing_content.collaborative);
                  latestIsDrawingCollaborativeRef.current = newData.drawing_content.collaborative;
                }
              }
            }
          }
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId, allSeiten]);

  // Keep refs updated with latest values for broadcast responses
  useEffect(() => { latestFormValuesRef.current = formValues; }, [formValues]);
  useEffect(() => { latestIsDrawingSharedRef.current = isDrawingShared; }, [isDrawingShared]);
  useEffect(() => { latestIsDrawingCollaborativeRef.current = isDrawingCollaborative; }, [isDrawingCollaborative]);
  useEffect(() => { latestCurrentSeiteIdRef.current = currentSeite?.id; }, [currentSeite?.id]);
  useEffect(() => { latestDrawingRef.current = drawingContent; }, [drawingContent]);
  
  // SYNCHRONOUS update in render body - ensures latest values are ALWAYS available
  // This solves the race condition where useEffect runs after the broadcast is sent
  getBroadcastStateRef.current = () => ({
    drawingContent,
    isDrawingShared,
    isDrawingCollaborative,
    formValues,
    currentSeiteId: currentSeite?.id,
    currentSeiteData: currentSeite,
  });

  // Broadcast state changes to pop-out customer tab
  useEffect(() => {
    if (!broadcastChannelRef.current) return;
    
    broadcastChannelRef.current.postMessage({
      type: 'beratung-sync',
      drawingContent,
      isDrawingShared,
      isDrawingCollaborative,
      formValues,
      currentSeiteId: currentSeite?.id,
      currentSeiteData: currentSeite,
    });
  }, [drawingContent, isDrawingShared, isDrawingCollaborative, formValues, currentSeite]);

  // Broadcast hover state changes to pop-out customer tab
  useEffect(() => {
    if (!broadcastChannelRef.current) return;
    
    broadcastChannelRef.current.postMessage({
      type: 'beratung-hover-sync',
      hoveredTileId,
    });
  }, [hoveredTileId]);

  // Handler for tile hover in advisor/customer panel
  const handleTileHover = useCallback((tileId: string | null) => {
    setHoveredTileId(tileId);
  }, []);

  // Handler for form field changes - persist to DB for cross-device sync
  const handleFieldChange = useCallback((fieldId: string, value: any) => {
    setFormValues(prev => {
      const newValues = { ...prev, [fieldId]: value };
      
      // Persist to DB for cross-device sync (debounced via mutation)
      if (sessionId) {
        updateSession.mutate({
          id: sessionId,
          form_values: newValues,
        });
      }
      
      return newValues;
    });
  }, [sessionId, updateSession]);

  const handlePrevPage = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPageIndex < totalPages - 1) {
      setCurrentPageIndex(prev => prev + 1);
    }
  };

  // Check if current page is "Prioritäten" and no priorities are selected
  const isPrioritaetenPage = currentSeite?.titel?.toLowerCase().includes('priorität');
  const selectedPriorities: string[] = formValues['selected_priorities'] || formValues['priorities'] || [];
  const canProceed = !isPrioritaetenPage || selectedPriorities.length > 0;

  // Get current module name
  const currentModuleName = useMemo(() => {
    if (!currentSeite) return '';
    const modul = alleModule.find(m => m.id === currentSeite.modul_id);
    return modul?.name || '';
  }, [currentSeite, alleModule]);

  // Handle opening customer view in new window
  const handleOpenKundenTab = useCallback(() => {
    if (!sessionId) return;
    const url = `/beratung/${sessionId}/kunden-view`;
    window.open(url, 'kundenView', 'width=1920,height=1080,menubar=no,toolbar=no');
  }, [sessionId]);

  // Generate share link for external participants
  const handleGenerateShareLink = useCallback(async () => {
    if (!sessionId) return;
    
    setIsGeneratingLink(true);
    setLinkCopied(false);
    
    try {
      // Check if token already exists for this session
      const { data: existingToken } = await supabase
        .from('beratung_session_tokens')
        .select('token')
        .eq('session_id', sessionId)
        .maybeSingle();
      
      let token: string;
      
      if (existingToken?.token) {
        token = existingToken.token;
      } else {
        // Generate new token
        token = crypto.randomUUID();
        
        const { error } = await fromTable('beratung_session_tokens')
          .insert({
            session_id: sessionId,
            token,
            permissions: 'interactive',
          });
        
        if (error) {
          console.error('Error creating share token:', error);
          return;
        }
      }
      
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/beratung-teilnehmen/${token}`;
      setShareLink(link);
    } catch (err) {
      console.error('Error generating share link:', err);
    } finally {
      setIsGeneratingLink(false);
    }
  }, [sessionId]);

  // Copy share link to clipboard
  const handleCopyLink = useCallback(async () => {
    if (!shareLink) return;
    
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  }, [shareLink]);

  // Open share dialog and generate link
  const handleOpenShareDialog = useCallback(() => {
    setIsShareDialogOpen(true);
    handleGenerateShareLink();
  }, [handleGenerateShareLink]);

  const renderModulKachel = (modul: BeratungModul) => {
    const isSelected = selectedModules.includes(modul.id);
    const icon = modul.icon ? ICON_MAP[modul.icon] || <Briefcase className="h-6 w-6" /> : <Briefcase className="h-6 w-6" />;

    return (
      <Card
        key={modul.id}
        onClick={() => toggleModule(modul.id)}
        className={cn(
          "cursor-pointer transition-all hover:shadow-md",
          isSelected 
            ? "border-primary bg-primary/5 ring-2 ring-primary/20" 
            : "hover:border-primary/50"
        )}
      >
        <CardContent className="p-4 flex flex-col items-center text-center gap-2">
          <div className={cn(
            "h-12 w-12 rounded-lg flex items-center justify-center",
            isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
          )}>
            {icon}
          </div>
          <h3 className="font-medium text-sm">{modul.name}</h3>
          {modul.beschreibung && (
            <p className="text-xs text-muted-foreground line-clamp-2">
              {modul.beschreibung}
            </p>
          )}
          {isSelected && (
            <Badge className="mt-1 bg-primary/10 text-primary">
              <Check className="h-3 w-3 mr-1" />
              Ausgewählt
            </Badge>
          )}
        </CardContent>
      </Card>
    );
  };

  // Module Selection View (Start)
  if (!isStarted) {
    return (
      <div className="h-screen flex flex-col bg-background">
        {/* Top Bar */}
        <div className="h-14 border-b flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Zurück
            </Button>
            <h1 className="font-semibold">Beratung - Modul Auswahl</h1>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {selectedModules.length} Module ausgewählt
            </Badge>
            <Button 
              onClick={handleStartBeratung}
              disabled={selectedModules.length === 0}
            >
              Beratung starten
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Module Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {modulesLoading ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-muted-foreground">Lade Module...</p>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto space-y-8">
              {/* Gruppe 1: Allgemein */}
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Allgemeine Beratung
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {gruppe1Module.map(renderModulKachel)}
                </div>
              </div>

              {/* Gruppe 2: Fachbereiche */}
              <div>
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Fachbereiche
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {gruppe2Module.map(renderModulKachel)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Active Beratung View (Split View / Mobile Tabs)
  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Top Bar */}
      <div className={cn(
        "border-b flex items-center justify-between px-3 flex-shrink-0",
        isMobile ? "h-auto py-2 flex-wrap gap-2" : "h-12"
      )}>
        {/* Left Section: Navigation */}
        <div className="flex items-center gap-1.5 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleEndBeratung}
            className="h-8 w-8 shrink-0"
            title="Zur Modulauswahl"
          >
            <Home className="h-4 w-4" />
          </Button>
          {!isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="h-8 w-8 shrink-0"
              title="Navigation ein-/ausblenden"
            >
              <PanelLeft className="h-4 w-4" />
            </Button>
          )}
          
          {/* Mobile: Show session name + date inline */}
          {isMobile && (
            <div className="flex items-center gap-2 min-w-0">
              {isRenamingSession ? (
                <Input
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  placeholder="Beratungsname..."
                  className="h-7 w-32 text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveSessionName();
                    if (e.key === 'Escape') {
                      setSessionName(session?.name || '');
                      setIsRenamingSession(false);
                    }
                  }}
                  onBlur={handleSaveSessionName}
                />
              ) : (
                <button
                  onClick={() => setIsRenamingSession(true)}
                  className="flex items-center gap-1 font-medium text-sm hover:text-primary transition-colors group"
                  title="Beratung umbenennen"
                >
                  <span className="truncate max-w-[120px]">{sessionName || 'Digitale Beratung'}</span>
                  <Edit3 className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
                </button>
              )}
            </div>
          )}
          
          {/* Desktop: Page counter + AutoSave */}
          {!isMobile && (
            <>
              <div className="w-px h-5 bg-border mx-1" />
              <Badge variant="secondary" className="text-xs shrink-0">
                {currentPageIndex + 1}/{totalPages || '...'}
              </Badge>
              <AutoSaveIndicator status={saveStatus} />
            </>
          )}
        </div>

        {/* Right Section: Actions */}
        <div className="flex items-center gap-1">
          {/* Admin Edit Mode Toggle */}
          {isAdmin && !isMobile && (
            <div className="flex items-center gap-1 px-2 border-r mr-1" title="Bearbeiten-Modus">
              <Settings className={cn(
                "h-3.5 w-3.5 transition-colors",
                isEditModeActive ? "text-primary" : "text-muted-foreground"
              )} />
              <Switch
                id="edit-mode"
                checked={isEditModeActive}
                onCheckedChange={setIsEditModeActive}
                className="scale-90"
              />
            </div>
          )}
          {!isMobile && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setIsNotizOpen(true)} className="h-8 px-2.5">
                <StickyNote className="h-4 w-4 mr-1.5" />
                <span className="text-xs">Notizen</span>
              </Button>
              <Button variant="ghost" size="sm" className="h-8 px-2.5">
                <FolderOpen className="h-4 w-4 mr-1.5" />
                <span className="text-xs">Ordner</span>
              </Button>
              <Button variant="ghost" size="sm" className="h-8 px-2.5">
                <Calculator className="h-4 w-4 mr-1.5" />
                <span className="text-xs">Rechner</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setIsDrawingOpen(true)} className="h-8 px-2.5">
                <Pencil className="h-4 w-4 mr-1.5" />
                <span className="text-xs">Zeichnen</span>
              </Button>
              <div className="w-px h-5 bg-border mx-0.5" />
              <Button variant="ghost" size="sm" onClick={handleOpenKundenTab} className="h-8 px-2.5">
                <ExternalLink className="h-4 w-4 mr-1.5" />
                <span className="text-xs">Kunden-Tab</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={handleOpenShareDialog} className="h-8 px-2.5">
                <Link2 className="h-4 w-4 mr-1.5" />
                <span className="text-xs">Teilen</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setIsHistoryOpen(true)} className="h-8 px-2.5">
                <History className="h-4 w-4 mr-1.5" />
                <span className="text-xs">Verlauf</span>
              </Button>
            </>
          )}
          {isMobile && (
            <>
              <Badge variant="secondary" className="text-xs">
                {currentPageIndex + 1}/{totalPages || '...'}
              </Badge>
              <Button variant="ghost" size="icon" onClick={() => setIsNotizOpen(true)} className="h-8 w-8">
                <StickyNote className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Progress Bar (only on mobile, sidebar shows progress on desktop) */}
      {isMobile && (
        <div className="px-4 py-2 border-b">
          <Progress value={progress} className="h-2" />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex min-h-0">
        {/* Sidebar (Desktop only) */}
        {!isMobile && (
          <BeratungSidebar
            isOpen={isSidebarOpen}
            onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
            modules={alleModule}
            selectedModuleIds={selectedModules}
            allSeiten={allSeiten}
            currentPageIndex={currentPageIndex}
            onPageSelect={setCurrentPageIndex}
            formValues={formValues}
            canNavigate={canProceed}
            sessionName={sessionName}
            sessionDate={sessionDate}
            onSessionNameChange={(name) => {
              setSessionName(name);
              if (sessionId) {
                updateSession.mutate({ id: sessionId, name });
              }
            }}
            onSessionDateChange={(date) => {
              if (date) {
                setSessionDate(date);
                if (sessionId) {
                  updateSession.mutate({ id: sessionId, gestartet_am: date.toISOString() });
                }
              }
            }}
          />
        )}

        {/* Split View (Desktop) or Tabs (Mobile) */}
        {isMobile ? (
          <BeratungMobileLayout
            seite={currentSeite}
            formValues={formValues}
            onFieldChange={handleFieldChange}
            isLoading={seitenLoading}
            interactiveTiles={true}
            onTileSelect={handleTileSelect}
            hoveredTileId={hoveredTileId}
            onTileHover={handleTileHover}
          />
        ) : seitenLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-muted-foreground">Lade Beratungsseiten...</p>
          </div>
        ) : (
          <ResizablePanelGroup direction="horizontal" className="flex-1">
            {/* Berater Panel */}
            <ResizablePanel defaultSize={40} minSize={30}>
              <BeraterPanel 
                seite={currentSeite} 
                formValues={formValues}
                onFieldChange={handleFieldChange}
              />
            </ResizablePanel>

            <ResizableHandle withHandle />

            {/* Kunden Panel */}
            <ResizablePanel defaultSize={60} minSize={40}>
            <KundenPanel 
              seite={currentSeite}
              formValues={formValues}
              onFieldChange={handleFieldChange}
              drawingContent={drawingContent}
              isDrawingShared={isDrawingShared}
              isDrawingCollaborative={isDrawingCollaborative}
              onDrawingChange={handleDrawingContentChange}
              drawingPlacement="inline"
              isEditModeActive={isEditModeActive}
              onTilesUpdate={handleTilesUpdate}
              interactiveTiles={true}
              onTileSelect={handleTileSelect}
              hoveredTileId={hoveredTileId}
              onTileHover={handleTileHover}
            />
            </ResizablePanel>
          </ResizablePanelGroup>
        )}
      </div>

      {/* Navigation Footer */}
      <div className={cn(
        "border-t flex items-center justify-between px-4 flex-shrink-0",
        isMobile ? "h-16 pb-safe" : "h-14"
      )}>
        <Button 
          variant="outline" 
          size={isMobile ? "sm" : "default"}
          disabled={currentPageIndex <= 0}
          onClick={handlePrevPage}
        >
          <ArrowLeft className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline">Zurück</span>
        </Button>
        <div className="flex items-center gap-1 md:gap-2">
          {allSeiten.slice(0, isMobile ? 5 : 10).map((seite, idx) => {
            const isDisabled = !canProceed && idx > currentPageIndex;
            return (
              <Tooltip key={seite.id}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => !isDisabled && setCurrentPageIndex(idx)}
                    disabled={isDisabled}
                    className={cn(
                      "w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all",
                      isDisabled
                        ? "bg-muted-foreground/20 cursor-not-allowed"
                        : idx === currentPageIndex 
                          ? "bg-primary w-4 md:w-6" 
                          : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                    )}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={8}>
                  <p>{seite.titel}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
          {allSeiten.length > (isMobile ? 5 : 10) && (
            <span className="text-xs text-muted-foreground">+{allSeiten.length - (isMobile ? 5 : 10)}</span>
          )}
        </div>
        <Button 
          size={isMobile ? "sm" : "default"}
          onClick={handleNextPage}
          disabled={currentPageIndex >= totalPages - 1 || !canProceed}
        >
          <span className="hidden md:inline">Weiter</span>
          <ArrowRight className="h-4 w-4 md:ml-2" />
        </Button>
      </div>

      {/* Notes Dialog */}
      <Dialog open={isNotizOpen} onOpenChange={setIsNotizOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Interne Notizen</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <RichTextEditorChecklist
              content={interneNotiz}
              onChange={setInterneNotiz}
              placeholder="Interne Notizen zur Beratung..."
              minHeight="400px"
              showProgress={false}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Drawing Dialog */}
      <Dialog 
        open={isDrawingOpen} 
        onOpenChange={(open) => {
          if (!open) {
            // Flush save when closing the dialog
            flushSave();
          }
          setIsDrawingOpen(open);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Zeichnen & Skizzieren</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <DrawingCanvas
              content={drawingContent}
              onChange={handleDrawingContentChange}
              isSharing={isDrawingShared}
              onSharingChange={handleSharingChange}
              isCollaborative={isDrawingCollaborative}
              onCollaborativeChange={handleCollaborativeChange}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Link Dialog */}
      <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Beratung teilen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Teilen Sie diesen Link mit Teilnehmern, damit diese auf einem anderen Gerät an der Beratung teilnehmen können.
            </p>
            
            {isGeneratingLink ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : shareLink ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Input 
                    value={shareLink} 
                    readOnly 
                    className="flex-1 text-sm"
                  />
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleCopyLink}
                    className="shrink-0"
                  >
                    {linkCopied ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-1 text-primary" />
                        Kopiert
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Kopieren
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Der Link ist gültig, solange die Beratungssitzung aktiv ist.
                </p>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* History Dialog */}
      <BeratungHistoryDialog
        open={isHistoryOpen}
        onOpenChange={setIsHistoryOpen}
        currentSessionId={sessionId}
        onSessionSelect={(newSessionId) => {
          setIsHistoryOpen(false);
          navigate(`/beratung/${newSessionId}`);
        }}
      />
    </div>
  );
}
